import { useState } from "react";
import { track } from "@/lib/analytics";

type Status = "idle" | "submitting" | "done" | "dup" | "error";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Notify me about new chronicles" waitlist capture. Writes one row to the
 * Supabase `waitlist` table (email + source). A unique-violation (already on
 * the list) is treated as a soft success, so a repeat sign-up never shows an
 * error. No PII touches analytics — only the bare `waitlist_signup` event
 * fires; the email lives solely in Supabase (no SELECT policy, so the list is
 * not publicly readable). Table + RLS: see docs/waitlist-setup.sql.
 */
export default function WaitlistSignup({ source = "startscreen" }: { source?: string }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "submitting") return;
    // Lower-cased so the unique constraint dedupes "Ann@x.com" and "ann@x.com";
    // 320 chars is the RFC 5321 maximum, also enforced by the table.
    const value = email.trim().toLowerCase();
    if (value.length > 320 || !EMAIL_RE.test(value)) {
      setStatus("error");
      return;
    }
    setStatus("submitting");
    try {
      // The `waitlist` table is provisioned out-of-band and is not yet in the
      // generated Supabase types, so cast to bypass the compile-time table check.
      // Runtime is unaffected — this still requires the table to exist in Supabase.
      // The client is loaded on demand so it stays off the first page.
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await (supabase as any).from("waitlist").insert({ email: value, source });
      if (error) {
        // 23505 = unique_violation → already on the list; treat as success.
        if ((error as { code?: string }).code === "23505") {
          setStatus("dup");
          return;
        }
        throw error;
      }
      track("waitlist_signup");
      setStatus("done");
    } catch (err) {
      console.error("Waitlist signup failed", err);
      setStatus("error");
    }
  };

  if (status === "done" || status === "dup") {
    return (
      <p
        className="font-body text-xs sm:text-sm text-gold-dim/90 italic"
        aria-live="polite"
      >
        {status === "dup"
          ? "You're already on the list: watch your inbox."
          : "You're on the list. New chronicles will find you."}
      </p>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-sm flex flex-col items-center gap-2"
      aria-live="polite"
    >
      <label
        htmlFor="waitlist-email"
        className="font-display text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-foreground/40 text-center"
      >
        New chronicles are added often: be the first to know
      </label>
      <div className="w-full flex gap-2">
        <input
          id="waitlist-email"
          type="email"
          maxLength={320}
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") setStatus("idle");
          }}
          placeholder="you@example.com"
          disabled={status === "submitting"}
          className="flex-1 min-w-0 px-3 py-2 min-h-[44px] bg-background/40 border border-border/50 rounded-sm font-body text-sm text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary/60 transition-colors"
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="px-4 py-2 min-h-[44px] font-display text-[11px] uppercase tracking-[0.15em] text-primary-foreground bg-primary/85 border border-primary rounded-sm hover:bg-primary transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          {status === "submitting" ? "…" : "Notify me"}
        </button>
      </div>
      {status === "error" && (
        <p className="font-body text-xs text-destructive/90" role="alert">
          Enter a valid email, or try again in a moment.
        </p>
      )}
    </form>
  );
}
